/**
 * Weather File List Component
 *
 * Displays a list of weather files with selection and preview actions.
 * Supports keyboard navigation and selection state indication.
 */

import { useCallback, useRef } from 'react';
import type { WeatherFileSearchResult } from '../../types';
import { WeatherFileListItem } from './WeatherFileListItem';

export interface WeatherFileListProps {
  files: WeatherFileSearchResult[];
  onSelect: (file: WeatherFileSearchResult) => void;
  onPreview: (file: WeatherFileSearchResult) => void;
  /** ID of currently selected file for highlighting */
  selectedFileId?: string | undefined;
}

export function WeatherFileList({ files, onSelect, onPreview, selectedFileId }: WeatherFileListProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // Keyboard navigation handler
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      const items = listRef.current?.querySelectorAll('[data-weather-file-item]');
      if (!items) return;

      let nextIndex = index;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          nextIndex = Math.min(index + 1, files.length - 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          nextIndex = Math.max(index - 1, 0);
          break;
        case 'Home':
          e.preventDefault();
          nextIndex = 0;
          break;
        case 'End':
          e.preventDefault();
          nextIndex = files.length - 1;
          break;
        default:
          return;
      }

      if (nextIndex !== index) {
        (items[nextIndex] as HTMLElement)?.focus();
      }
    },
    [files.length]
  );

  if (files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="mb-4 text-muted-foreground"
          aria-hidden="true"
        >
          <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
        </svg>
        <h3 className="mb-2 text-lg font-medium">No weather files found</h3>
        <p className="text-sm text-muted-foreground">
          Try adjusting your search or filters
        </p>
      </div>
    );
  }

  return (
    <div
      ref={listRef}
      className="space-y-2"
      role="listbox"
      aria-label="Weather files"
    >
      {files.map((file, index) => (
        <WeatherFileListItem
          key={file.id}
          file={file}
          onSelect={() => onSelect(file)}
          onPreview={() => onPreview(file)}
          isSelected={file.id === selectedFileId}
          onKeyDown={(e) => handleKeyDown(e, index)}
        />
      ))}
    </div>
  );
}
